/**
 * API service for communicating with the Django backend.
 * Uses JSON API endpoints for clean frontend-backend communication.
 */

import { apiRequest, getCSRFToken, API_BASE } from './apiBase';

// ============ Product analytics ============

/**
 * Mark that the player typed their FIRST letter in a game — the real
 * "started playing" signal (top of the engagement funnel). Best-effort:
 * never throws and never blocks gameplay.
 */
export async function markEngaged({ gameId = null, mode = null, lang = 'en', platform = null } = {}) {
    try {
        // '/pulse/' — innocuous path so ad-blockers don't eat the engaged event
        // (the old '/track/engaged/' was filtered on the 'track' token).
        return await apiRequest('/pulse/', {
            method: 'POST',
            body: JSON.stringify({ gameId, mode, lang, platform }),
        });
    } catch (e) {
        return null;
    }
}

/**
 * First-party copy of an ad impression (Plausible's copy is often ad-blocked).
 * Best-effort: never throws.
 */
export async function recordAdImpression({ adType, position = null } = {}) {
    try {
        return await apiRequest('/pulse/e/', {
            method: 'POST',
            body: JSON.stringify({ event: 'ad_impression', ad_type: adType, position }),
        });
    } catch (e) {
        return null;
    }
}

/**
 * Push opt-in funnel step (shown, tap, result) so we can see where reminders fail. Best-effort: never throws.
 */
export async function recordPushPrompt({ step, surface, outcome = null }) {
    try {
        return await apiRequest('/pulse/e/', {
            method: 'POST',
            body: JSON.stringify({ event: 'push_prompt', step, surface, outcome }),
        });
    } catch (e) {
        return null;
    }
}

/**
 * "Get the app" link on the victory card (shown, tap). Best-effort: never throws.
 */
export async function recordAppInvite({ step, os }) {
    try {
        return await apiRequest('/pulse/e/', {
            method: 'POST',
            body: JSON.stringify({ event: 'app_invite', step, os }),
        });
    } catch (e) {
        return null;
    }
}

/**
 * Page-load timings (ms; cls unitless) for real-user performance by platform.
 * keepalive lets it finish while the page is being hidden. Best-effort: never throws.
 */
export async function recordWebVitals(sample) {
    try {
        return await apiRequest('/pulse/e/', {
            method: 'POST',
            keepalive: true,
            body: JSON.stringify({ event: 'web_vitals', ...sample }),
        });
    } catch (e) {
        return null;
    }
}

/**
 * Record first-touch acquisition (UTM params + referrer). Called once per
 * browser on first load. Best-effort: never throws.
 */
export async function sendAttribution(payload) {
    try {
        // '/ref/' — innocuous path (the old '/attribution/' was ad-blocked).
        return await apiRequest('/ref/', {
            method: 'POST',
            body: JSON.stringify(payload || {}),
        });
    } catch (e) {
        return null;
    }
}

// ============ Shared results ============

export async function createShare(payload) {
    return apiRequest('/share/', { method: 'POST', body: JSON.stringify(payload) });
}

export async function fetchShare(slug) {
    return apiRequest(`/share/${encodeURIComponent(slug)}/`);
}

export async function submitShareResult(slug, path) {
    return apiRequest(`/share/${encodeURIComponent(slug)}/result/`, {
        method: 'POST',
        body: JSON.stringify({ path }),
    });
}

// Best-effort: records that the player actually sent the link (and how).
export async function markShareSent(slug, method) {
    try {
        return await apiRequest(`/share/${encodeURIComponent(slug)}/sent/`, {
            method: 'POST',
            body: JSON.stringify({ method }),
        });
    } catch (e) {
        return null;
    }
}

// ============ Connectivity ============

// True when the backend answers its health check; used to leave demo mode after a deploy.
export async function pingBackend(timeoutMs = 5000) {
    const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
    try {
        const res = await fetch(`${API_BASE}/health/`, { cache: 'no-store', signal: ctrl?.signal });
        return res.ok;
    } catch {
        return false;
    } finally {
        if (timer) clearTimeout(timer);
    }
}

// ============ Authentication ============

export async function checkAuthStatus() {
    return apiRequest('/auth/status/');
}

export async function login(email, password) {
    return apiRequest('/auth/login/', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
    });
}

export async function signup(email, password, username = '') {
    return apiRequest('/auth/signup/', {
        method: 'POST',
        body: JSON.stringify({ email, password, username }),
    });
}

export async function logout() {
    return apiRequest('/auth/logout/', {
        method: 'POST',
    });
}

export async function socialLogin(payload) {
    return apiRequest('/auth/social/', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function resendVerification(email) {
    return apiRequest('/auth/resend-verification/', {
        method: 'POST',
        body: JSON.stringify({ email }),
    });
}

export async function updateProfile(username) {
    return apiRequest('/auth/update-profile/', {
        method: 'POST',
        body: JSON.stringify({ username }),
    });
}

// ============ Game ============

export async function fetchGame(lang = 'en', mode = 'ranked', options = {}) {
    const { difficulty = 1, startWord, targetWord, step } = options;

    let url = `/${lang}/game/?mode=${encodeURIComponent(mode)}`;
    if (mode === 'random' || mode === 'expert') {
        url += `&difficulty=${encodeURIComponent(difficulty)}`;
    } else if (mode === 'custom' && startWord && targetWord) {
        url += `&start_word=${encodeURIComponent(startWord)}&target_word=${encodeURIComponent(targetWord)}`;
    } else if (mode === 'onboarding') {
        url += `&step=${encodeURIComponent(step || 1)}`;
    }

    return apiRequest(url);
}

export async function submitGame(lang = 'en', steps, mode = 'ranked', elapsedSeconds = null, distanceIndicatorUsed = true, step = null) {
    const payload = { action: 'submit', steps };
    if (elapsedSeconds !== null) payload.elapsedSeconds = elapsedSeconds;
    payload.distanceIndicatorUsed = distanceIndicatorUsed;
    if (mode === 'onboarding' && step) payload.step = step;
    let url = `/${lang}/game/?mode=${encodeURIComponent(mode)}`;
    if (mode === 'onboarding' && step) url += `&step=${encodeURIComponent(step)}`;
    return apiRequest(url, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function nextGame(lang = 'en', mode = 'ranked', options = {}) {
    const { difficulty = 1, startWord, targetWord, step } = options;

    let url = `/${lang}/game/?mode=${encodeURIComponent(mode)}`;
    if (mode === 'random' || mode === 'expert') {
        url += `&difficulty=${encodeURIComponent(difficulty)}`;
    } else if (mode === 'custom' && startWord && targetWord) {
        url += `&start_word=${encodeURIComponent(startWord)}&target_word=${encodeURIComponent(targetWord)}`;
    } else if (mode === 'onboarding') {
        url += `&step=${encodeURIComponent(step || 1)}`;
    }

    return apiRequest(url, {
        method: 'POST',
        body: JSON.stringify({ action: 'next' }),
    });
}

export async function previousGame(lang = 'en', mode = 'ranked') {
    return apiRequest(`/${lang}/game/?mode=${mode}`, {
        method: 'POST',
        body: JSON.stringify({ action: 'back' }),
    });
}

export async function validateWord(lang = 'en', word) {
    return apiRequest(`/${lang}/validate-word/`, {
        method: 'POST',
        body: JSON.stringify({ word }),
    });
}

export async function getHint(lang = 'en', pathData, mode = 'ranked') {
    return apiRequest(`/${lang}/hint/`, {
        method: 'POST',
        body: JSON.stringify({ pathData, mode }),
    });
}

export async function applyHint(lang = 'en', hintId) {
    return apiRequest(`/${lang}/hint/apply/`, {
        method: 'POST',
        body: JSON.stringify({ hintId }),
    });
}

export async function getHintHistory(lang = 'en', mode = 'ranked') {
    return apiRequest(`/${lang}/hint/history/?mode=${mode}`);
}

/**
 * Record a hint computed client-side to the server for tracking.
 * @param {string} lang - Language code
 * @param {Object} hintData - Hint details
 * @param {string} hintData.hintType - 'add' or 'remove_add'
 * @param {string} hintData.hintWord - The suggested word
 * @param {number} hintData.wordsToRemove - Number of words to remove (0 for add only)
 * @param {string|null} hintData.removeFrom - 'top', 'bottom', or null
 * @param {boolean} hintData.applied - Whether user applied the hint
 * @param {boolean} hintData.failed - Whether hint computation failed
 * @param {string} mode - Game mode ('ranked', 'random', etc.)
 */
export async function recordHint(lang = 'en', hintData, mode = 'ranked') {
    return apiRequest(`/${lang}/hint/record/`, {
        method: 'POST',
        body: JSON.stringify({ ...hintData, mode }),
    });
}

// ============ Ladder System ============

/**
 * Fetch the complete ladder with all games for ranked mode.
 * Returns all games upfront for instant navigation.
 */
export async function fetchLadder(lang = 'en', mode = 'ranked', options = {}) {
    const { difficulty = 1 } = options;

    let url = `/${lang}/ladder/?mode=${mode}`;
    if (mode === 'random') {
        url += `&difficulty=${difficulty}`;
    }

    return apiRequest(url);
}

/**
 * Submit a game solution in the new ladder system.
 * @param {string} lang - Language code
 * @param {number} gameIndex - Index of the game in the ladder (0-9)
 * @param {string[]} steps - Array of words in the solution path
 */
// Practice-mode stats + personal bests (B20). Best-effort: returns null on error.
export async function fetchPracticeStats(lang = 'en') {
    try {
        return await apiRequest(`/practice-stats/?lang=${lang}`);
    } catch (e) {
        return null;
    }
}

export async function submitLadderGame(lang = 'en', gameIndex, steps, elapsedSeconds = null, distanceIndicatorUsed = true) {
    const payload = { action: 'submit', gameIndex, steps };
    if (elapsedSeconds !== null) payload.elapsedSeconds = elapsedSeconds;
    payload.distanceIndicatorUsed = distanceIndicatorUsed;
    return apiRequest(`/${lang}/ladder/?mode=ranked`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

// Tell the server a ladder/challenge game was opened, so its row carries the real start time.
export async function startLadderGame(lang = 'en', mode = 'ranked', gameIndex) {
    const url = mode === 'challenge' ? `/${lang}/challenge/` : `/${lang}/ladder/?mode=ranked`;
    return apiRequest(url, {
        method: 'POST',
        body: JSON.stringify({ action: 'start', gameIndex }),
    });
}

/**
 * Get next random game at specified difficulty (1-10) in new tier system.
 */
export async function nextRandomGame(lang = 'en', difficulty = 1) {
    return apiRequest(`/${lang}/ladder/?mode=random&difficulty=${difficulty}`, {
        method: 'POST',
        body: JSON.stringify({ action: 'next' }),
    });
}

// ============ Game State Persistence ============

export async function saveGameState(lang = 'en', gameId, mode, gameIndex, state) {
    return apiRequest(`/${lang}/game/save-state/`, {
        method: 'POST',
        body: JSON.stringify({ gameId, mode, gameIndex, state }),
    });
}

/**
 * Save game state during page unload. We deliberately do NOT use
 * navigator.sendBeacon: it cannot attach custom headers, so the request
 * would lose the X-CSRFToken header that the save-state endpoint requires.
 * `fetch(..., { keepalive: true })` keeps the request alive past unload AND
 * lets us send the CSRF header — strictly better.
 */
export function sendBeaconSaveState(lang = 'en', gameId, mode, gameIndex, state) {
    const url = `${API_BASE}/${encodeURIComponent(lang)}/game/save-state/`;
    const payload = JSON.stringify({ gameId, mode, gameIndex, state });
    try {
        fetch(url, {
            method: 'POST',
            body: payload,
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCSRFToken() || '',
            },
            credentials: 'include',
            keepalive: true,
        }).catch(() => {});
        return true;
    } catch {
        return false;
    }
}

// ============ Word Info ============

export async function getWordInfo(lang = 'en', word) {
    return apiRequest(`/${lang}/word-info/?word=${encodeURIComponent(word)}`);
}

// ============ Levels ============

export async function fetchLevels(lang = 'en') {
    return apiRequest(`/${lang}/levels/`);
}

// ============ Leaderboard ============

export async function fetchLeaderboard(lang = 'en', period = 'daily', page = 1, limit = 50) {
    return apiRequest(`/${lang}/leaderboard/?period=${period}&page=${page}&limit=${limit}`);
}

// ============ User Stats ============

export async function fetchUserStats() {
    return apiRequest('/stats/');
}

// ============ Achievements ============

export async function fetchAchievements() {
    return apiRequest('/achievements/');
}

// ============ Streak ============

export async function fetchStreak() {
    return apiRequest('/streak/');
}

export async function claimStreakMilestone(milestone) {
    return apiRequest('/streak/claim/', {
        method: 'POST',
        body: JSON.stringify({ milestone }),
    });
}

export async function buyStreakFreeze(count = 1) {
    return apiRequest('/streak/buy-freeze/', {
        method: 'POST',
        body: JSON.stringify({ count }),
    });
}

export async function recoverStreak() {
    return apiRequest('/streak/recover/', {
        method: 'POST',
    });
}

// ============ Push Notifications ============

export async function registerPushSubscription(subscriptionData) {
    return apiRequest('/push/register/', {
        method: 'POST',
        body: JSON.stringify(subscriptionData),
    });
}

export async function unregisterPushSubscription(data) {
    return apiRequest('/push/unregister/', {
        method: 'POST',
        body: JSON.stringify(data),
    });
}

export async function fetchPushStatus() {
    return apiRequest('/push/status/');
}

export async function sendFeedback(payload) {
    return apiRequest('/feedback/', {
        method: 'POST',
        body: JSON.stringify(payload || {}),
    });
}

export async function updatePushPreferences(enabled, timezone) {
    const body = { enabled };
    if (timezone) body.timezone = timezone;
    return apiRequest('/push/preferences/', {
        method: 'POST',
        body: JSON.stringify(body),
    });
}

// ============ Skip & Star Goals ============

/**
 * Skip a game in the daily ladder and reveal the solution.
 * One free skip per day.
 */
export async function skipLadderGame(lang = 'en', gameIndex) {
    return apiRequest(`/${lang}/ladder/?mode=ranked`, {
        method: 'POST',
        body: JSON.stringify({ action: 'skip', gameIndex }),
    });
}

/**
 * Fetch the current user's daily star goal progress.
 */
export async function fetchStarGoal(lang = 'en') {
    return apiRequest(`/${lang}/star-goal/`);
}

// ============ Weekly Challenge ============

export async function fetchChallenge(lang = 'en') {
    return apiRequest(`/${lang}/challenge/`);
}

export async function submitChallengeGame(lang = 'en', gameIndex, steps, elapsedSeconds = null, distanceIndicatorUsed = true) {
    const payload = { action: 'submit', gameIndex, steps };
    if (elapsedSeconds !== null) payload.elapsedSeconds = elapsedSeconds;
    payload.distanceIndicatorUsed = distanceIndicatorUsed;
    return apiRequest(`/${lang}/challenge/`, {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}

export async function fetchChallengeLeaderboard(lang = 'en', page = 1, limit = 50) {
    return apiRequest(`/${lang}/challenge/leaderboard/?page=${page}&limit=${limit}`);
}

// ============ Expert Mode ============

export async function fetchExpertLeaderboard(lang = 'en', category = 'total_score', limit = 50, period = 'all') {
    return apiRequest(`/${lang}/expert-leaderboard/?category=${category}&limit=${limit}&period=${period}`);
}

// ============ Practice Mode ============

export async function fetchPracticeLeaderboard(lang = 'en', category = 'total_score', limit = 50, period = 'all') {
    return apiRequest(`/${lang}/practice-leaderboard/?category=${category}&limit=${limit}&period=${period}`);
}

// ============ Rank Progress ============

export async function fetchRankProgress() {
    return apiRequest('/rank-progress/');
}

// ============ Skill Rating ============

export async function fetchPlayerRating() {
    return apiRequest('/rating/');
}

export async function fetchRatingHistory(period = '30d', limit = 100) {
    return apiRequest(`/rating/history/?period=${period}&limit=${limit}`);
}

export async function fetchRatingLeaderboard(lang = 'en', limit = 50, tier = '', period = 'all') {
    const params = new URLSearchParams({ limit, period });
    if (tier) params.append('tier', tier);
    return apiRequest(`/${lang}/rating/leaderboard/?${params}`);
}

// ============ Game History ============

export async function fetchGameHistory(lang = 'en', mode = 'random', page = 1) {
    return apiRequest(`/${lang}/game-history/?mode=${mode}&page=${page}`);
}

// ============ User Preferences (cross-device sync) ============

export async function getPreferences() {
    return apiRequest('/preferences/');
}

export async function setPreferences(prefs) {
    return apiRequest('/preferences/', {
        method: 'POST',
        body: JSON.stringify({ preferences: prefs }),
    });
}

// ============ GDPR / Account ============

export async function deleteAccount() {
    return apiRequest('/account/delete/', {
        method: 'POST',
    });
}

export async function exportData() {
    return apiRequest('/account/export/');
}

// Export all as default object for convenience
const api = {
    pingBackend,
    // Auth
    checkAuthStatus,
    login,
    signup,
    logout,

    // Game (legacy API)
    fetchGame,
    submitGame,
    nextGame,
    previousGame,
    validateWord,
    getHint,
    applyHint,
    getHintHistory,
    recordHint,

    // Ladder System
    fetchLadder,
    submitLadderGame,
    startLadderGame,
    fetchPracticeStats,
    nextRandomGame,
    skipLadderGame,
    fetchStarGoal,

    // Game State Persistence
    saveGameState,
    sendBeaconSaveState,

    // Word Info
    getWordInfo,

    // Levels
    fetchLevels,

    // Leaderboard
    fetchLeaderboard,

    // Stats
    fetchUserStats,

    // Achievements
    fetchAchievements,

    // Streak
    fetchStreak,
    claimStreakMilestone,
    buyStreakFreeze,

    // Push Notifications
    registerPushSubscription,
    unregisterPushSubscription,
    updatePushPreferences,
    fetchPushStatus,
    recordPushPrompt,
    sendFeedback,

    // Weekly Challenge
    fetchChallenge,
    submitChallengeGame,
    fetchChallengeLeaderboard,

    // Expert Mode
    fetchExpertLeaderboard,
    fetchPracticeLeaderboard,

    // Rank Progress
    fetchRankProgress,

    // Skill Rating
    fetchPlayerRating,
    fetchRatingHistory,
    fetchRatingLeaderboard,

    // Game History
    fetchGameHistory,

    // User Preferences
    getPreferences,
    setPreferences,

    // GDPR / Account
    deleteAccount,
    exportData,

    // Shared results
    createShare,
    fetchShare,
    submitShareResult,
    markShareSent,

    // Product analytics
    markEngaged,
    sendAttribution,

    // Helpers
    getCSRFToken,
};

export default api;
